/**
 * Everything the player expects to survive a reload.
 *
 * Storage is treated as optional throughout: private windows, disabled
 * cookies and full quotas all have to degrade to an in-memory session rather
 * than take the game down with them.
 */

import { detectLanguage, isLanguage, type Language } from "./i18n";

export interface Preferences {
  readonly soundEnabled: boolean;
  readonly volume: number;
  readonly statsVisible: boolean;
  /** The FPS readout in the top-right corner. */
  readonly fpsVisible: boolean;
  /** Colour-blind mode: shapes alongside the runway/aircraft colours. */
  readonly symbolsVisible: boolean;
  readonly language: Language;
  readonly tutorialSeen: boolean;
}

export interface BestRun {
  readonly landings: number;
  readonly departures: number;
  readonly durationSeconds: number;
}

/** A finished run, kept locally so the player can see their own last few. */
export interface RunRecord extends BestRun {
  /** ISO timestamp of the moment the run ended. */
  readonly finishedAt: string;
}

/** How many finished runs are kept. Older ones fall off the end. */
export const HISTORY_LIMIT = 10;

export interface PreferenceStore {
  readonly preferences: Preferences;
  update(patch: Partial<Preferences>): Preferences;
  readonly best: BestRun | null;
  /** The last {@link HISTORY_LIMIT} finished runs, newest first. */
  readonly history: readonly RunRecord[];
  /**
   * Records a finished run; returns true when it beat the stored best.
   *
   * Every run joins the history, including a scoreless one: the board refuses
   * those, so the local list is the only place a bad run is ever acknowledged.
   */
  recordRun(run: BestRun, finishedAt?: Date): boolean;
}

/** Minimal slice of the Storage API, so tests can hand in a fake. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const PREFERENCES_KEY = "pixelflightcontrol.preferences";
const BEST_KEY = "pixelflightcontrol.best";
const HISTORY_KEY = "pixelflightcontrol.runs";

function defaultPreferences(languages?: readonly string[]): Preferences {
  return {
    soundEnabled: true,
    volume: 0.7,
    statsVisible: true,
    fpsVisible: true,
    symbolsVisible: false,
    language: detectLanguage(languages),
    tutorialSeen: false,
  };
}

function browserStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Reading `localStorage` itself throws when storage is blocked.
    return null;
  }
}

function read(storage: StorageLike | null, key: string): unknown {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(key);
    return raw === null ? null : JSON.parse(raw);
  } catch {
    // Corrupt or unreadable: fall back to defaults rather than crash.
    return null;
  }
}

function write(storage: StorageLike | null, key: string, value: unknown): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or a locked-down browser; the session keeps its values in memory.
  }
}

function boolOr(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function volumeOr(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.max(0, Math.min(1, value));
}

function countOr(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : 0;
}

/**
 * Read a stored history back.
 *
 * Anything unreadable is dropped rather than repaired: the history is a
 * convenience, and a half-parsed row would show as a run of zeroes the player
 * never flew.
 */
function parseHistory(value: unknown): RunRecord[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const runs: RunRecord[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const row = item as Partial<RunRecord>;
    const finishedAt =
      typeof row.finishedAt === "string" &&
      Number.isFinite(new Date(row.finishedAt).getTime())
        ? row.finishedAt
        : null;
    if (!finishedAt) {
      continue;
    }
    runs.push({
      landings: countOr(row.landings),
      departures: countOr(row.departures),
      durationSeconds: countOr(row.durationSeconds),
      finishedAt,
    });
  }
  return runs.slice(0, HISTORY_LIMIT);
}

/** A run beats the record on landings first, then departures, then speed. */
export function isBetterRun(candidate: BestRun, best: BestRun | null): boolean {
  if (!best) {
    return true;
  }
  if (candidate.landings !== best.landings) {
    return candidate.landings > best.landings;
  }
  if (candidate.departures !== best.departures) {
    return candidate.departures > best.departures;
  }
  return candidate.durationSeconds < best.durationSeconds;
}

export interface PreferenceStoreOptions {
  readonly storage?: StorageLike | null;
  /** Navigator languages, used only when nothing has been stored yet. */
  readonly languages?: readonly string[];
}

export function createPreferenceStore(
  options: PreferenceStoreOptions = {},
): PreferenceStore {
  const storage =
    options.storage === undefined ? browserStorage() : options.storage;

  const stored = read(storage, PREFERENCES_KEY) as Partial<Preferences> | null;
  const fallback = defaultPreferences(options.languages);
  let preferences: Preferences = {
    soundEnabled: boolOr(stored?.soundEnabled, fallback.soundEnabled),
    volume: volumeOr(stored?.volume, fallback.volume),
    statsVisible: boolOr(stored?.statsVisible, fallback.statsVisible),
    fpsVisible: boolOr(stored?.fpsVisible, fallback.fpsVisible),
    symbolsVisible: boolOr(stored?.symbolsVisible, fallback.symbolsVisible),
    language: isLanguage(stored?.language)
      ? stored.language
      : fallback.language,
    tutorialSeen: boolOr(stored?.tutorialSeen, fallback.tutorialSeen),
  };

  const storedBest = read(storage, BEST_KEY) as Partial<BestRun> | null;
  let best: BestRun | null = storedBest
    ? {
        landings: countOr(storedBest.landings),
        departures: countOr(storedBest.departures),
        durationSeconds: countOr(storedBest.durationSeconds),
      }
    : null;
  // A stored record with no landings is not a record.
  if (best && best.landings === 0) {
    best = null;
  }

  let history = parseHistory(read(storage, HISTORY_KEY));

  return {
    get preferences() {
      return preferences;
    },
    get best() {
      return best;
    },
    get history() {
      return history;
    },
    update(patch) {
      preferences = {
        ...preferences,
        ...patch,
        volume: volumeOr(
          patch.volume ?? preferences.volume,
          preferences.volume,
        ),
      };
      write(storage, PREFERENCES_KEY, preferences);
      return preferences;
    },
    recordRun(run, finishedAt = new Date()) {
      const candidate: BestRun = {
        landings: countOr(run.landings),
        departures: countOr(run.departures),
        durationSeconds: Math.max(0, run.durationSeconds),
      };

      history = [
        { ...candidate, finishedAt: finishedAt.toISOString() },
        ...history,
      ].slice(0, HISTORY_LIMIT);
      write(storage, HISTORY_KEY, history);

      if (candidate.landings === 0 || !isBetterRun(candidate, best)) {
        return false;
      }
      best = candidate;
      write(storage, BEST_KEY, best);
      return true;
    },
  };
}
